import { cn } from './cn'

export { cn }

export type BtnVariant = 'default' | 'primary' | 'ghost' | 'danger'
export type BtnSize = 'md' | 'sm'

const btnBase =
  'inline-flex items-center justify-center gap-1.5 rounded-[10px] border text-sm font-medium no-underline transition disabled:cursor-not-allowed disabled:opacity-55'

const btnVariants: Record<BtnVariant, string> = {
  default:
    'border-su-border bg-su-surface text-su-text-strong shadow-su-sm hover:bg-su-surface-muted',
  primary:
    'border-su-primary bg-su-primary text-white shadow-sm hover:border-su-primary-hover hover:bg-su-primary-hover',
  ghost: 'border-transparent bg-transparent text-su-text-strong shadow-none hover:bg-su-surface-muted',
  danger:
    'border-red-300/50 bg-red-50/60 text-red-700 shadow-su-sm hover:bg-red-100/80 dark:border-red-500/40 dark:bg-red-950/40 dark:text-red-300',
}

const btnSizes: Record<BtnSize, string> = {
  md: 'px-4 py-2',
  sm: 'px-2.5 py-1 text-xs',
}

export function btn(variant: BtnVariant = 'default', size: BtnSize = 'md', extra?: string) {
  return cn(btnBase, btnVariants[variant], btnSizes[size], extra)
}

export const inputClass =
  'w-full rounded-[10px] border border-su-border bg-su-surface px-3 py-2.5 text-[inherit] transition focus:border-su-primary focus:outline-none focus:ring-[3px] focus:ring-su-primary/35'

export const searchInputClass = cn(
  inputClass,
  'max-w-96 rounded-full px-4 py-2 text-sm shadow-su-sm placeholder:text-slate-400',
)

export const labelClass = 'text-xs font-semibold tracking-wide text-su-text'

export const pageTitleClass = 'm-0 text-xl font-bold tracking-tight text-su-text-strong'

export const pageLeadClass = 'mt-1.5 max-w-2xl text-[0.92rem] leading-relaxed text-su-text'

export const sectionTitleClass = 'm-0 mb-2 text-xl font-bold tracking-tight text-su-text-strong'

export const pageContentClass = 'min-w-0 w-full'

export const alertBase = 'mb-4 rounded-su px-4 py-3.5 text-sm leading-snug'

export function alertClass(kind: 'err' | 'warn') {
  return cn(
    alertBase,
    kind === 'err'
      ? 'border border-red-300/45 bg-gradient-to-br from-red-50 to-red-100 text-red-900 dark:from-red-950 dark:to-red-900 dark:text-red-200'
      : 'border border-amber-300/45 bg-gradient-to-br from-amber-50 to-amber-100 text-amber-900 dark:from-amber-950 dark:to-amber-900 dark:text-amber-200',
  )
}

/** @deprecated используйте alertClass */
export const alert = alertClass

export const tableWrapClass =
  'overflow-auto rounded-su-lg border border-su-border bg-su-surface shadow-su-md'

export const tableClass = 'w-full border-collapse text-sm'

export const tableCellClass = 'border-b border-su-border px-4 py-2.5 text-left'

export const tableHeadClass = cn(
  tableCellClass,
  'bg-su-surface-muted text-[0.8125rem] font-semibold tracking-tight text-slate-500',
)

export const tableCompactCellClass = cn(tableCellClass, 'py-1.5 text-xs')

export const tableCompactHeadClass = cn(tableHeadClass, 'py-1.5 text-xs')

export const tableRowHoverClass = 'transition hover:bg-indigo-500/4'

export const tableLastRowClass = '[&_tr:last-child_td]:border-b-0'

export const toolbarClass = 'mb-4 flex flex-wrap items-center gap-3'

export const toolbarSearchWrapClass = 'flex min-w-40 flex-1 justify-end'

export const formGridClass = 'grid max-w-lg gap-4'

export const formActionsClass = 'mt-2 flex flex-wrap gap-2'

export const loadingClass =
  'flex min-h-[45vh] items-center justify-center text-[0.95rem] font-medium text-su-text'

export const cardClass =
  'rounded-su-lg border border-su-border bg-su-surface p-5 shadow-su-md'

export const loginPageClass =
  'flex min-h-svh items-center justify-center bg-gradient-to-b from-slate-50 via-slate-100 to-slate-200 p-4 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950'

/** Список продукции/расходников с деревом категорий */
export const productionWithCategoriesClass =
  'flex flex-row items-start gap-5 max-[900px]:flex-col-reverse'

export const productionMainClass = 'min-w-0 flex-1'

export const categoryTreeAsideClass = cn(
  'sticky top-3 w-[272px] shrink-0 overflow-auto rounded-su-lg border border-su-border bg-su-surface p-3 shadow-su-md',
  'max-h-[calc(100vh-4rem)] text-sm max-[900px]:relative max-[900px]:top-0 max-[900px]:max-h-70 max-[900px]:w-full',
)

/** Раздел «Администрирование» */
export const adminSectionClass = cn(
  'grid items-start gap-x-8 gap-y-5',
  'grid-cols-[13rem_minmax(0,1fr)] [grid-template-areas:"header_header"_"nav_main"]',
  'max-md:grid-cols-1 max-md:gap-4 max-md:[grid-template-areas:"header"_"nav"_"main"]',
)

export const adminSectionHeaderClass = 'min-w-0 [grid-area:header]'

export const adminSectionNavClass = 'sticky top-4 min-w-0 [grid-area:nav] max-md:static'

export const adminSectionMainClass = 'min-w-0 [grid-area:main]'

export function linkLikeClass(extra?: string) {
  return cn(
    'cursor-pointer border-none border-b border-transparent bg-transparent p-0 text-left text-sm font-medium text-su-primary no-underline transition',
    'hover:border-current hover:text-su-primary-hover',
    extra,
  )
}

/** Глобальный сайдбар приложения */
export function sidebarNavLink(isActive: boolean) {
  return cn(
    'block rounded-[10px] px-3 py-2.5 text-[0.92rem] font-medium no-underline transition',
    isActive
      ? 'bg-white/12 font-semibold text-white shadow-sm'
      : 'text-slate-300 hover:bg-white/8 hover:text-white',
  )
}

/** Подменю раздела «Администрирование» */
export function adminNavLink(isActive: boolean, mobile = false) {
  return cn(
    'block rounded-[10px] px-3 py-2 text-[0.92rem] font-medium no-underline transition',
    isActive
      ? 'bg-indigo-500/10 font-semibold text-su-primary'
      : 'text-su-text hover:bg-su-surface-muted hover:text-su-text-strong',
    mobile && 'max-md:border max-md:border-su-border max-md:bg-su-surface',
    mobile && isActive && 'max-md:border-indigo-300/35',
  )
}

/** Горизонтальные вкладки */
export function tabLink(isActive: boolean) {
  return cn(
    'rounded-[10px] px-4 py-2 text-[0.92rem] font-medium no-underline transition',
    isActive
      ? 'bg-su-surface font-semibold text-su-primary shadow-su-sm'
      : 'text-su-text hover:bg-white/70 hover:text-su-text-strong dark:hover:bg-white/10',
  )
}

export const tabsWrapClass =
  'inline-flex flex-wrap gap-1 rounded-[14px] border border-su-border bg-su-surface-muted p-1.5'

export const permGroupClass =
  'rounded-su border border-su-border bg-su-surface-muted px-4 py-3.5'

export const permGroupTitleClass = 'mb-2 text-sm font-semibold text-su-text-strong'

/** Выпадающий список (портал) — ConsumablePicker, SearchableSelect */
export const pickerPanelClass = cn(
  'fixed z-[10060] box-border flex max-h-[min(70vh,22rem)] min-h-0 flex-col overflow-hidden',
  'rounded-su border border-su-border bg-su-surface shadow-su-md',
)

export const pickerListClass = 'm-0 min-h-0 flex-1 list-none overflow-y-auto py-1.5'

export function pickerOptionClass(active: boolean) {
  return cn(
    'flex w-full cursor-pointer flex-col items-start gap-0.5 border-none bg-transparent px-3 py-2 text-left font-[inherit] text-su-text-strong transition',
    'hover:bg-indigo-500/8',
    active && 'bg-indigo-500/14 shadow-[inset_3px_0_0_var(--color-su-primary)]',
  )
}

export function pickerFieldClass(open: boolean, extra?: string) {
  return cn(
    'flex w-full items-stretch rounded-[10px] border border-su-border bg-su-surface transition',
    'hover:border-slate-300',
    open && 'border-su-primary shadow-[0_0_0_3px] shadow-su-primary/35',
    'focus-within:border-su-primary focus-within:shadow-[0_0_0_3px] focus-within:shadow-su-primary/35',
    extra,
  )
}

export function categoryTreeChipClass(active: boolean) {
  return cn(
    'cursor-pointer rounded-full border px-2.5 py-0.5 text-xs font-[inherit] transition',
    active
      ? 'border-blue-300 bg-blue-50 font-medium text-blue-700 dark:border-blue-500/40 dark:bg-blue-950/50 dark:text-blue-300'
      : 'border-su-border bg-su-surface-muted text-su-text hover:bg-su-surface',
  )
}
